
import { User, UserStatus } from "../types";
import { api } from "./api";

// Default Badges definition for merging (since we store only IDs in DB for now)
const DEFAULT_BADGES = [
  { id: '1', name: 'Math Whiz', icon: '🧮', description: 'Score 100% on a Math quiz', unlocked: false },
  { id: '2', name: 'Science Star', icon: '🚀', description: 'Complete 5 Science quizzes', unlocked: false },
  { id: '3', name: 'Bookworm', icon: '📚', description: 'Read 3 passages', unlocked: false },
  { id: '4', name: 'Historian', icon: '🏛️', description: 'Ace a history test', unlocked: false },
];

let currentUserCache: User | null = null;

export const authService = {
  init: async () => {
    // Check if we have a token and validate it
    const token = localStorage.getItem('clevercubby_token');
    if (token) {
      try {
        const user = await api.get('/auth/me');
        currentUserCache = mapUserResponse(user);
      } catch (e) {
        localStorage.removeItem('clevercubby_token');
      }
    }
  },

  login: async (email: string, password: string) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      localStorage.setItem('clevercubby_token', res.token);
      // Fetch full profile immediately
      const user = await api.get('/auth/me');
      currentUserCache = mapUserResponse(user);
      return { success: true, user: currentUserCache };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },

  register: async (data: any) => {
    try {
      const res = await api.post('/auth/register', data);
      localStorage.setItem('clevercubby_token', res.token);
      currentUserCache = mapUserResponse(res.user);
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },


  googleLogin: async (credential: string, grade?: number, state?: string) => {
    try {
      const res = await api.post('/auth/google', { credential, grade, state });

      if (res.requiresOnboarding) {
        // New user needs to provide grade and state
        return { success: false, requiresOnboarding: true, credential };
      }

      localStorage.setItem('clevercubby_token', res.token);
      currentUserCache = mapUserResponse(res.user);
      return { success: true, user: currentUserCache, isNewUser: res.isNewUser };
    } catch (err: any) {
      // Check if this is a 400 error with requiresOnboarding flag
      if (err.data?.requiresOnboarding) {
        return { success: false, requiresOnboarding: true, credential };
      }
      return { success: false, message: err.message || err.data?.message };
    }
  },

  logout: () => {
    localStorage.removeItem('clevercubby_token');
    currentUserCache = null;
  },

  getCurrentUser: (): User | null => {
    // If we have a cached user, return it
    if (currentUserCache) {
      return currentUserCache;
    }

    // If no cache but we have a token, try to restore from localStorage
    const token = localStorage.getItem('clevercubby_token');
    if (token) {
      // Trigger a refresh in the background
      authService.refreshUser().catch(() => {
        // If refresh fails, clear the token
        localStorage.removeItem('clevercubby_token');
      });
    }

    return currentUserCache;
  },

  refreshUser: async (): Promise<User | null> => {
    try {
      const user = await api.get('/auth/me');
      currentUserCache = mapUserResponse(user);
      return currentUserCache;
    } catch (e) {
      console.error("Failed to refresh user", e);
      // Clear invalid token
      localStorage.removeItem('clevercubby_token');
      currentUserCache = null;
      return null;
    }
  },

  updateCurrentUser: async (updates: Partial<User>) => {
    try {
      const result = await api.patch('/auth/me', updates);
      if (result.success && result.user) {
        currentUserCache = mapUserResponse(result.user);
        return { success: true, user: currentUserCache };
      }
      return { success: false, message: 'Failed to update profile' };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },

  getAllUsers: async (): Promise<User[]> => {
    try {
      const users = await api.get('/admin/users');
      return users.map((u: any) => mapUserResponse(u));
    } catch (err) {
      console.error('Failed to fetch users:', err);
      return [];
    }
  },

  updateUserStatus: async (userId: string, status: UserStatus) => {
    try {
      const result = await api.patch(`/admin/users/${userId}/status`, { status });
      return { success: true, user: result.user };
    } catch (err: any) {
      return { success: false, message: err.message };
    }
  },

  getSystemAnalytics: async () => {
    try {
      const analytics = await api.get('/admin/analytics');
      return analytics;
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      return null;
    }
  }
};

// Helper to merge DB user with static badge definitions
function mapUserResponse(dbUser: any): User {
  const badges = DEFAULT_BADGES.map(b => ({
    ...b,
    unlocked: dbUser.unlockedBadgeIds?.includes(b.id) || false
  }));
  return { ...dbUser, badges };
}

// Initial check
authService.init();
